// 非編集時のテキスト / 付箋本文を描画するコンポーネント。本文中の URL を自動でハイパーリンク化する。
//
// クリック挙動（要件④）:
// - 確定後（非編集）の単一クリック: 少し遅延してから開く。遅延中にダブルクリック（＝編集開始）が
//   来たら開くのをキャンセルし、コンテナ側の onDoubleClick（編集モード遷移）へ譲る。
// - 修飾キー（Ctrl/Cmd/Shift/Alt）付きクリックや中クリックは、<a> の標準挙動に委ねる。
// - 参照専用（埋め込み）は編集が無いため、単一クリックで即開く。
// リンクは data-canvas-link="true" を持ち、use-object-drag はこの上での pointerdown で選択/ドラッグを
// 開始しない（クリックがリンク遷移として成立する）。
//
// リンク表示仕様:
// - 外部リンク（オリジンが異なる）: 新規タブ + rel="noopener noreferrer" + 末尾に
//   「新しいタブを開きます」アイコン（クリック可能領域はアイコンまで含む）
// - 内部リンク（同一オリジン）: 同一タブ遷移・アイコンなし
// - 表示テキストが URL そのもののリンクは 1 行省略表示（…）とし、title でフル URL を確認できる
// - 表示テキスト付きリンク（[テキスト](URL)）は表示テキストのままリンク化する

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { TextInlineDisplaySpan } from "@refinement-canvas/domain";
import { notifyEmbedSearchDismissed } from "../../../auth/embed-auth";
import { isExternalHttpUrl, splitTextIntoLinkSegments } from "../../../lib/linkify-text";
import { normalizeSearchTerms, splitTextBySearchTerms } from "../../../lib/search-highlight";
import {
  resolveTextInlineEffectiveBackground,
  textInlineDecorationClassName,
  textInlineStyleCss,
} from "../../../lib/text-inline-style-css";
import {
  resolveMarkdownTableForeground,
  resolveMarkdownTableLinkColorVars,
  type MarkdownTableBodyCellContext,
} from "../../../lib/text-link-color";
import { useCanvasStore } from "../store/canvas-store";

// 単一クリックで開くまでの遅延。この間にダブルクリックが来たら編集へ切り替えるためキャンセルする。
const OPEN_DELAY_MS = 250;

/**
 * 検索語ハイライトの描画に必要な情報。
 * 参照専用の埋め込み表示で、親から検索語を受け取っているときだけ作られる。
 */
export interface SearchHighlightContext {
  /** 正規化済みの照合語（1 つ以上）。 */
  readonly terms: readonly string[];
  /** 強調の解除（検索語を空にし、親へ通知する）。 */
  readonly onDismiss: () => void;
}

/**
 * 現在の検索語から、描画用の context を作る。強調しないとき（検索語なし）は undefined を返す。
 *
 * 検索語は参照専用でしか store へ入らないため、編集ビューでは常に undefined になる。
 */
export function useSearchHighlightContext(): SearchHighlightContext | undefined {
  const searchQuery = useCanvasStore((s) => s.searchQuery);
  const terms = useMemo(() => normalizeSearchTerms(searchQuery), [searchQuery]);
  // setter は store から購読せず getState() で取る。この hook は本文の行・区間ごとに呼ばれるため、
  // 購読を 1 本（searchQuery だけ）に抑え、pan / zoom のたびに走る選択子の数を増やさない。
  const onDismiss = useCallback(() => {
    // 1 か所だけを消すのではなく、検索語そのものを空にして全 <mark> を消す。
    useCanvasStore.getState().setSearchQuery("");
    notifyEmbedSearchDismissed();
  }, []);
  return useMemo(
    () => (terms.length === 0 ? undefined : { onDismiss, terms }),
    [onDismiss, terms]
  );
}

/**
 * 表示文字へ検索語の強調を重ねる。強調しないときは元の文字列をそのまま返す
 * （検索語が無い場合の DOM 構造を、この機能の追加前と完全に同じに保つ）。
 *
 * `dismissible` が false の一致（リンクの内側・リンクカードのタイトル）は解除用の属性・ハンドラを
 * 持たない。クリックは親の `<a>`（またはリンクカード）へ渡り、従来どおりリンク先へ遷移する。
 */
export function renderSearchHighlightedText(
  text: string,
  search: SearchHighlightContext | undefined,
  options: { readonly dismissible: boolean }
): ReactNode {
  if (search === undefined || text.length === 0) return text;
  const segments = splitTextBySearchTerms(text, search.terms);
  if (!segments.some((segment) => segment.matched)) return text;
  // key は分割結果のインデックス。segments は同じ本文と同じ語から毎回同じ順序で導出される。
  return segments.map((segment, index) => {
    if (!segment.matched) return <Fragment key={index}>{segment.value}</Fragment>;
    if (!options.dismissible) return <mark key={index}>{segment.value}</mark>;
    return (
      <mark
        key={index}
        // role="button" を付けると要素の内容ではなく aria-label が読み上げ名になるため、
        // 一致した文字そのものを名前へ含める（本文から一致語が消えて聞こえないようにする）。
        aria-label={`${segment.value}（検索のハイライトを解除）`}
        data-canvas-search-hit="true"
        role="button"
        tabIndex={0}
        onClick={(event) => {
          // オブジェクトの選択やパン保留の開始へ伝播させない。
          // preventDefault は行わない（文字選択やリンクの既定動作を壊さないため）。
          event.stopPropagation();
          search.onDismiss();
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          // role="button" の作法どおり、Space による画面スクロールだけは抑える。
          event.preventDefault();
          event.stopPropagation();
          search.onDismiss();
        }}
      >
        {segment.value}
      </mark>
    );
  });
}

function openUrl(url: string, external: boolean, readOnly: boolean): void {
  // 外部リンク、または参照専用（埋め込み iframe）表示のときは新規タブで開く。
  // 埋め込み時に内部リンクを同一タブ遷移させると iframe の内容が置き換わり、
  // 閲覧者が埋め込みから抜け出せなくなるため、readOnly では内部リンクも新規タブにする。
  if (external || readOnly) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  // 通常表示の内部リンク（同一オリジン）は同一タブで遷移する。
  window.location.assign(url);
}

/**
 * 外部リンク末尾のアイコン（別サイトへ別タブで遷移することを示す）。
 * 装飾ではなく意味を持つため、支援技術向けの代替テキストを付与する。
 * コメント本文のリンク描画（LinkifiedCommentText）でも同じ見た目を共有するため export する。
 */
export function ExternalLinkIcon(): ReactNode {
  return (
    <svg
      aria-label="新しいタブを開きます"
      className="external-link-icon"
      fill="none"
      role="img"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.2}
      viewBox="0 0 24 24"
    >
      <path d="M7 17 17 7M7 7h10v10" />
    </svg>
  );
}

/**
 * リンクのクリック挙動（単一クリックの遅延オープンと、ダブルクリックでのキャンセル）を
 * 提供する共有フック。素の LinkifiedText と、部分書式つきの StyledLinkifiedText が共有する。
 */
function useLinkClickHandler(): (
  url: string,
  external: boolean,
  readOnly: boolean
) => (event: {
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  detail: number;
  preventDefault: () => void;
}) => void {
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    };
  }, []);
  return (url, external, readOnly) => (event) => {
    // 修飾キー付きクリックはブラウザ標準（新規タブ / バックグラウンドタブ）に委ねる。
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (readOnly) {
      openUrl(url, external, readOnly);
      return;
    }
    // ダブルクリックの 2 回目（detail>=2）なら開くのをやめ、編集モード遷移へ譲る。
    if (event.detail >= 2) {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      return;
    }
    // 単一クリック: 少し遅延して開く（この間にダブルクリックが来たら上でキャンセルされる）。
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    pendingTimerRef.current = setTimeout(() => {
      pendingTimerRef.current = null;
      openUrl(url, external, readOnly);
    }, OPEN_DELAY_MS);
  };
}

export function LinkifiedText({ text }: { readonly text: string }): JSX.Element {
  const readOnly = useCanvasStore((s) => s.readOnly);
  const makeClickHandler = useLinkClickHandler();
  const search = useSearchHighlightContext();

  // 検索語の分割は、リンク分割の各セグメントの内側だけで行う（リンク判定を壊さないための順序）。
  const segments = splitTextIntoLinkSegments(text);

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "text" || !segment.url) {
          // key はセグメント列のインデックス。text は再レンダーのたびに再分割されるため安定。
          return (
            <span key={index}>
              {renderSearchHighlightedText(segment.value, search, { dismissible: true })}
            </span>
          );
        }
        const url = segment.url;
        const external = isExternalHttpUrl(url);
        // 表示テキストが URL そのもの（自動リンク化）の場合のみ 1 行省略表示の対象にする。
        const bareUrl = segment.value === url;
        const className = [
          "canvas-linkified-link",
          ...(bareUrl ? ["canvas-linkified-link--url"] : []),
        ].join(" ");
        return (
          <a
            key={index}
            className={className}
            href={url}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            draggable={false}
            data-canvas-link="true"
            title={bareUrl ? url : undefined}
            onClick={makeClickHandler(url, external, readOnly)}
          >
            {bareUrl ? (
              <span className="canvas-linkified-link-label">
                {renderSearchHighlightedText(segment.value, search, { dismissible: false })}
              </span>
            ) : (
              renderSearchHighlightedText(segment.value, search, { dismissible: false })
            )}
            {external ? <ExternalLinkIcon /> : null}
          </a>
        );
      })}
    </>
  );
}

/**
 * 表示区間（部分書式 + 記法の装飾）を `<span>` の列として描く。
 *
 * キャンバスオブジェクト（StyledLinkifiedText）とコメント本文（LinkifiedCommentText）は
 * リンクの挙動が異なるため、共用するのはこの「装飾つき区間の描画」だけにする。
 * アンカー（`<a>`）の生成・属性・イベントハンドラは、それぞれの実装のままにする。
 */
export function renderInlineDecoratedSpans(
  spans: readonly TextInlineDisplaySpan[],
  options?: {
    readonly headingLine?: boolean | undefined;
    readonly tableBodyContext?: MarkdownTableBodyCellContext | undefined;
    readonly link?: boolean | undefined;
    /**
     * 検索語ハイライトを重ねる場合の context。渡さない呼び出し（コメント本文・表オブジェクト）は
     * 従来どおり素のテキストを描く。
     */
    readonly searchHighlight?: SearchHighlightContext | undefined;
  }
): JSX.Element[] {
  // key は分割結果のインデックス。spans は本文と run から毎回同じ順序で導出されるため安定。
  return spans.map((span, index) => {
    const decorationClassName = textInlineDecorationClassName(span.decoration, {
      codeEnd: span.codeEnd,
      codeStart: span.codeStart,
    });
    let css: CSSProperties = textInlineStyleCss(span.style, {
      decoration: span.decoration,
      headingLine: options?.headingLine,
    });
    const tableBodyContext = options?.tableBodyContext;
    if (tableBodyContext !== undefined) {
      const effectiveBackground = resolveTextInlineEffectiveBackground(
        span.style,
        span.decoration,
        tableBodyContext.background
      );
      if (options?.link === true) {
        // リンク状態は CSS セレクターから切り替えるため、通常の inline color は出力しない。
        css = {
          ...css,
          color: undefined,
          ...resolveMarkdownTableLinkColorVars({
            explicitColor: textInlineStyleCss(span.style).color,
            effectiveBackground,
            onDark: tableBodyContext.palette.onDark,
          }),
        };
      } else {
        css = {
          ...css,
          color: resolveMarkdownTableForeground(
            typeof css.color === "string" ? css.color : tableBodyContext.bodyColor,
            effectiveBackground
          ),
        };
      }
    }
    const className = [
      decorationClassName,
      ...(tableBodyContext !== undefined && options?.link === true
        ? ["canvas-md-table-link-run"]
        : []),
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <span key={index} className={className || undefined} style={css}>
        {renderSearchHighlightedText(span.text, options?.searchHighlight, {
          dismissible: options?.link !== true,
        })}
      </span>
    );
  });
}

/** 連続する同一リンク（または非リンク）の区間を 1 まとめにした描画単位。 */
interface StyledLinkGroup {
  readonly url: string | undefined;
  readonly spans: readonly TextInlineDisplaySpan[];
}

/**
 * 分割結果を「連続する同じ URL（および非リンク）」でまとめる。
 * 1 つのリンクを書式境界で複数の `<a>` へ割ると、素 URL の 1 行省略表示・`title`・外部リンク
 * アイコンが区間ごとの判定になってしまうため、リンクは 1 グループ = 1 anchor で描く。
 */
function groupSpansByLink(spans: readonly TextInlineDisplaySpan[]): readonly StyledLinkGroup[] {
  const groups: { url: string | undefined; spans: TextInlineDisplaySpan[] }[] = [];
  for (const span of spans) {
    const last = groups[groups.length - 1];
    // 非リンク（url なし）どうしも 1 グループへまとめる（描画は区間ごとの span のまま）。
    if (last && last.url === span.url) {
      last.spans.push(span);
      continue;
    }
    groups.push({ spans: [span], url: span.url });
  }
  return groups;
}

/**
 * 部分書式・記法の装飾つきの本文 1 行を描画する。分割（リンク境界 × run 境界 × 装飾境界）は
 * domain の共有関数が済ませており、ここでは各区間へ CSS とクラスを当てて描くだけにする。
 *
 * - リンクが書式境界で分割されても、同じ URL の区間はまとめて 1 つの安全な anchor として描く
 *   （素 URL の 1 行省略表示・`title`・外部リンクアイコンの契約を、分割前と同じに保つ）。
 * - 部分太字 false は、見出し行では行の太字を打ち消さない（意味上の装飾を維持する）。
 * - 記法の太字は、run が bold を明示していないときだけ採用する（textInlineStyleCss の規則）。
 */
export function StyledLinkifiedText({
  spans,
  prefix,
  prefixClassName,
  prefixStyle,
  headingLine,
  tableBodyContext,
}: {
  readonly spans: readonly TextInlineDisplaySpan[];
  /** 箇条書きのグリフや番号付きリストの項番など、run を持たない表示専用の接頭辞。 */
  readonly prefix?: string | undefined;
  /** 接頭辞の span に付けるクラス（番号付きリストの項番 span など）。省略時は属性を付けない。 */
  readonly prefixClassName?: string | undefined;
  /** 接頭辞の span へ与えるインラインスタイル（マーカー領域の幅）。省略時は属性を付けない。 */
  readonly prefixStyle?: CSSProperties | undefined;
  readonly headingLine?: boolean | undefined;
  /** 表本文セル内だけで使う、run ごとの実効背景・前景色補正 context。 */
  readonly tableBodyContext?: MarkdownTableBodyCellContext | undefined;
}): JSX.Element {
  const readOnly = useCanvasStore((s) => s.readOnly);
  const makeClickHandler = useLinkClickHandler();
  const search = useSearchHighlightContext();
  const renderPieces = (group: StyledLinkGroup): JSX.Element[] =>
    renderInlineDecoratedSpans(group.spans, {
      headingLine,
      tableBodyContext,
      link: group.url !== undefined,
      searchHighlight: search,
    });
  return (
    <>
      {prefix !== undefined && prefix.length > 0 ? (
        <span className={prefixClassName} style={prefixStyle}>
          {prefix}
        </span>
      ) : null}
      {groupSpansByLink(spans).map((group, index) => {
        const url = group.url;
        if (url === undefined) {
          return <Fragment key={index}>{renderPieces(group)}</Fragment>;
        }
        const external = isExternalHttpUrl(url);
        // 素の URL（表示テキスト = URL）だけを 1 行省略表示の対象にする既存契約を維持する。
        // 判定はグループ全体の表示テキストで行う（書式境界で分割されても契約を変えない）。
        const displayText = group.spans.map((span) => span.text).join("");
        const bareUrl = displayText === url;
        const className = [
          "canvas-linkified-link",
          ...(bareUrl ? ["canvas-linkified-link--url"] : []),
        ].join(" ");
        const tableLinkStyle =
          tableBodyContext === undefined
            ? undefined
            : (resolveMarkdownTableLinkColorVars({
                effectiveBackground: tableBodyContext.background,
                onDark: tableBodyContext.palette.onDark,
              }) as CSSProperties);
        return (
          <a
            key={index}
            className={className}
            href={url}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            draggable={false}
            data-canvas-link="true"
            title={bareUrl ? url : undefined}
            onClick={makeClickHandler(url, external, readOnly)}
            style={tableLinkStyle}
          >
            {bareUrl ? (
              <span className="canvas-linkified-link-label">{renderPieces(group)}</span>
            ) : (
              renderPieces(group)
            )}
            {external ? <ExternalLinkIcon /> : null}
          </a>
        );
      })}
    </>
  );
}
